/**
 * First-cut live-price extraction for the native (mobile) refresh path.
 *
 * On web the Gloss extension parses the store page and hands back a clean
 * price. On the native app there is no extension, so the page is fetched via
 * Capacitor (see gloss-grocery.component's fetchPriceViaCapacitor) and a price
 * is pulled out of the raw HTML/JSON here. Intentionally conservative: it tries
 * the structured signals a store page usually carries and returns null rather
 * than guessing, so a bot-wall challenge page (which has no price) is reported
 * as "couldn't read a fresh price" instead of a bogus number. Refine per store
 * once verified on a device.
 */
export function extractLivePrice(body: string): number | null {
  if (!body) {
    return null;
  }

  // 1. schema.org JSON-LD `offers.price` — common across retailers. Parse the
  //    structured data (rather than flat-regex it) so a nested `offers` object
  //    — one carrying a priceSpecification, seller, shippingDetails, etc.
  //    before the price — is still read correctly, and so the price is taken
  //    from `offers` specifically rather than the first "price" seen anywhere
  //    (which could be a was-price, a related product, or a priceSpecification).
  const fromStructured = priceFromStructuredData(body);
  if (fromStructured != null) {
    return round2(fromStructured);
  }

  // 2. Generic current-price key: `"now": 6` (Coles) / `"Price": 3.55`
  //    (Woolworths). Last resort for pages without usable JSON-LD. Anchored on
  //    a quoted key so it matches an exact `price`/`now` key, not a substring
  //    such as `unitPrice` or `wasPrice`.
  const generic = body.match(/"(?:price|now)"\s*:\s*"?([0-9]+(?:\.[0-9]+)?)"?/i);
  const fromGeneric = generic ? Number(generic[1]) : NaN;
  if (Number.isFinite(fromGeneric) && fromGeneric > 0) {
    return round2(fromGeneric);
  }

  return null;
}

/**
 * Finds an `offers.price` by parsing the page's JSON-LD block(s) — or, when the
 * fetched body is itself a JSON document, that document — and walking it for
 * the first `offers` node with a positive price. Returns null if nothing parses
 * or no offers price is found.
 */
function priceFromStructuredData(body: string): number | null {
  const candidates: string[] = [];

  // JSON-LD embedded in an HTML page: <script type="application/ld+json">…</script>.
  const scriptRe = /<script[^>]*type=["']application\/ld\+json["'][^>]*>([\s\S]*?)<\/script>/gi;
  let match: RegExpExecArray | null;
  while ((match = scriptRe.exec(body)) !== null) {
    candidates.push(match[1]);
  }
  // Or the body may itself be a JSON document (native fetch returned JSON).
  if (candidates.length === 0) {
    candidates.push(body);
  }

  for (const raw of candidates) {
    const parsed = tryParseJson(raw);
    if (parsed === undefined) {
      continue;
    }
    const price = findOffersPrice(parsed);
    if (price != null) {
      return price;
    }
  }
  return null;
}

/** Recursively finds the first `offers` node carrying a positive price. */
function findOffersPrice(node: unknown): number | null {
  if (Array.isArray(node)) {
    for (const element of node) {
      const price = findOffersPrice(element);
      if (price != null) {
        return price;
      }
    }
    return null;
  }
  if (node && typeof node === "object") {
    const obj = node as Record<string, unknown>;
    if ("offers" in obj) {
      const price = priceFromOffers(obj.offers);
      if (price != null) {
        return price;
      }
    }
    for (const key of Object.keys(obj)) {
      const price = findOffersPrice(obj[key]);
      if (price != null) {
        return price;
      }
    }
  }
  return null;
}

/** Reads a positive `price` (or `lowPrice`) from a single Offer or an array of them. */
function priceFromOffers(offers: unknown): number | null {
  if (Array.isArray(offers)) {
    for (const offer of offers) {
      const price = priceFromOffers(offer);
      if (price != null) {
        return price;
      }
    }
    return null;
  }
  if (offers && typeof offers === "object") {
    const o = offers as Record<string, unknown>;
    return toPositiveNumber(o.price) ?? toPositiveNumber(o.lowPrice);
  }
  return null;
}

/** Parses JSON, returning undefined (never throwing) on malformed input. */
function tryParseJson(raw: string): unknown {
  try {
    return JSON.parse(raw);
  } catch {
    return undefined;
  }
}

/** Coerces a number or numeric string to a finite, positive number, else null. */
function toPositiveNumber(value: unknown): number | null {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
